import { useState, useEffect, useRef, useMemo } from "react";

const INITIAL_LEVEL = 0.65;
const NUM_BUBBLES = 35;

// ─── DRAIN PHYSICS ────────────────────────────────────────────────────────
// Drain is now driven by the Y-component of gravity in SCREEN coordinates.
// gyn ∈ [-1, +1]
//   gyn = +1  → gravity points DOWN on screen (phone upright, normal)
//   gyn =  0  → gravity is parallel to top/bottom edge (phone on its side)
//   gyn = -1  → gravity points UP on screen (top of phone tipped down → "drinking")
// Liquid only spills out the TOP of the screen when gyn is sufficiently negative.
// Pure sideways tilt (gamma alone) leaves gyn near 0, so no drain. ✓
const TILT_DEADZONE = 3;
const DRAIN_RATE = 0.01; // per-frame drain at max pull (was 0.018 — gentler)
const DRAIN_GY_START = 0.3; // |gyn| needed to *start* draining
const DRAIN_GY_FULL = 0.7; // |gyn| at which drain reaches max rate
const CALIB_SAMPLES = 30;
const GAMMA_FLIP_GUARD = 85;

// ─── POUR-IN ANIMATION ────────────────────────────────────────────────────
const POUR_DURATION_MS = 1400;
const POUR_DELAY_MS = 250;
const TEXT_DELAY_MS = POUR_DELAY_MS + POUR_DURATION_MS + 200;

const SHOW_DEBUG = process.env.NODE_ENV !== "production";

const TOAST_TITLES = [
  "Bien merecida, bb ✨",
  "Eso sí estuvo icónico 🍺",
  "Bb, sí te ganaste la caguama 🍺",
];
const TOAST_SUBTITLES = [
  "Respeto para esa racha.",
  "Esa estuvo poderosa, bb.",
  "No cualquiera llega hasta aquí.",
  "Ya quedó bautizada esa racha.",
  "Kari estaría orgullosa 😌",
  "Te luciste, la verdad.",
  "Ya puedes presumir este logro 👑",
];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ─── Helper: get real viewport height (fixes PWA/iOS innerHeight issues) ──
const getVH = () => document.documentElement.clientHeight || window.innerHeight;

export default function CaguamaAnimation({ onClose, streak }) {
  const canvasRef = useRef(null);
  const volRef = useRef(0);
  const targetVolRef = useRef(0);

  const isPouringRef = useRef(true);
  const [isPouring, setIsPouring] = useState(true);

  const gammaEffRef = useRef(0);
  const betaEffRef = useRef(0);

  const gravXRef = useRef(0);
  const gravYRef = useRef(1);
  const targetGravXRef = useRef(0);
  const targetGravYRef = useRef(1);

  const calibSamplesRef = useRef({ gamma: [], beta: [] });
  const calibratedRef = useRef(false);
  const baseGammaRef = useRef(0);
  const baseBetaRef = useRef(0);

  const rafRef = useRef(null);
  const isDrainingRef = useRef(false);
  const drainDoneRef = useRef(false);
  const hasVibratedRef = useRef(false);
  const forceDrainRef = useRef(false);

  const bubblesRef = useRef(
    Array.from({ length: NUM_BUBBLES }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: 2 + Math.random() * 7,
      speed: 0.0004 + Math.random() * 0.0005,
      opacity: 0.15 + Math.random() * 0.25,
    }))
  );

  const [showToast, setShowToast] = useState(false);
  const [isDraining, setIsDraining] = useState(false);
  const [showContinueBtn, setShowContinueBtn] = useState(false);
  const [showTexts, setShowTexts] = useState(false);

  // ─── iOS PERMISSION STATE ─────────────────────────────────────────────
  // "checking"    → probing whether events fire (iOS, on mount)
  // "idle"        → needs the tap button (iOS, no events received)
  // "asking"      → requestPermission() in flight
  // "granted"     → sensor active
  // "denied"      → sensor blocked
  // "unavailable" → DeviceOrientationEvent doesn't exist (desktop, etc.)
  const [iosPermission, setIosPermission] = useState(() => {
    if (typeof DeviceOrientationEvent === "undefined") return "unavailable";
    // Android / non-iOS: no requestPermission function → already granted
    if (typeof DeviceOrientationEvent.requestPermission !== "function")
      return "granted";
    // iOS: probe first before deciding to show the button
    return "checking";
  });

  const [debugInfo, setDebugInfo] = useState({
    gammaRaw: 0,
    betaRaw: 0,
    gammaRel: 0,
    betaRel: 0,
    gammaEff: 0,
    betaEff: 0,
    gravX: 0,
    gravY: 1,
    drainPull: 0,
    drainAmount: 0,
    calib: false,
    calibCount: 0,
    flipGuard: false,
    vol: 0,
  });
  const debugUpdateRef = useRef(0);

  const toastMessage = useMemo(
    () => ({ title: pick(TOAST_TITLES), subtitle: pick(TOAST_SUBTITLES) }),
    [] // eslint-disable-line
  );

  // ─── iOS PERMISSION: called on user tap ──────────────────────────────
  const requestIOSPermission = async () => {
    if (typeof DeviceOrientationEvent?.requestPermission !== "function") return;
    setIosPermission("asking");
    try {
      const result = await DeviceOrientationEvent.requestPermission();
      setIosPermission(result === "granted" ? "granted" : "denied");
    } catch {
      setIosPermission("denied");
    }
  };

  // ─── PROBE: detect if iOS sensor permission was already granted ──────
  // If events fire within 500ms of mount, permission was granted earlier
  // (e.g. user accepted in a previous component mount within the same PWA
  // session). In that case skip straight to "granted" — no button flash.
  useEffect(() => {
    if (iosPermission !== "checking") return;

    let receivedEvent = false;
    let cancelled = false;

    const probe = (e) => {
      // iOS without permission fires no events at all (or events with all-null values).
      if (e.gamma !== null || e.beta !== null) {
        receivedEvent = true;
      }
    };

    window.addEventListener("deviceorientation", probe);

    const timeout = setTimeout(() => {
      if (cancelled) return;
      window.removeEventListener("deviceorientation", probe);
      setIosPermission(receivedEvent ? "granted" : "idle");
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      window.removeEventListener("deviceorientation", probe);
    };
  }, [iosPermission]);

  // ─── POUR-IN ANIMATION on mount ──────────────────────────────────────
  useEffect(() => {
    const pourStart = performance.now() + POUR_DELAY_MS;
    let pourRaf;

    const animatePour = (now) => {
      if (now < pourStart) {
        pourRaf = requestAnimationFrame(animatePour);
        return;
      }
      const elapsed = now - pourStart;
      const t = Math.min(1, elapsed / POUR_DURATION_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      targetVolRef.current = eased * INITIAL_LEVEL;

      if (t < 1) {
        pourRaf = requestAnimationFrame(animatePour);
      } else {
        isPouringRef.current = false;
        setIsPouring(false);
        if (navigator.vibrate) navigator.vibrate(20);
      }
    };
    pourRaf = requestAnimationFrame(animatePour);

    const textTimer = setTimeout(() => setShowTexts(true), TEXT_DELAY_MS);

    return () => {
      cancelAnimationFrame(pourRaf);
      clearTimeout(textTimer);
    };
  }, []);

  // ─── RENDER LOOP ─────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = document.documentElement.clientWidth || window.innerWidth;
      canvas.height = getVH();
    };
    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("orientationchange", () => setTimeout(resize, 200));

    const tick = () => {
      const ctx = canvas.getContext("2d");
      const W = canvas.width;
      const H = canvas.height;
      const cx = W / 2;
      const cy = H / 2;

      volRef.current += (targetVolRef.current - volRef.current) * 0.07;
      gravXRef.current += (targetGravXRef.current - gravXRef.current) * 0.1;
      gravYRef.current += (targetGravYRef.current - gravYRef.current) * 0.1;

      const vol = Math.max(0, Math.min(1, volRef.current));

      const gMag = Math.hypot(gravXRef.current, gravYRef.current) || 1;
      const gxn = gravXRef.current / gMag;
      const gyn = gravYRef.current / gMag;

      ctx.clearRect(0, 0, W, H);

      const threshold = findSurfaceThreshold(W, H, cx, cy, gxn, gyn, vol);

      const screenRect = [
        [0, 0],
        [W, 0],
        [W, H],
        [0, H],
      ];
      const liquidPoly = clipHalfPlane(screenRect, gxn, gyn, threshold, cx, cy);

      const surfaceCenterX = cx + gxn * threshold;
      const surfaceCenterY = cy + gyn * threshold;

      if (liquidPoly.length >= 3) {
        const liquidGrad = ctx.createLinearGradient(
          surfaceCenterX,
          surfaceCenterY,
          surfaceCenterX + gxn * H,
          surfaceCenterY + gyn * H
        );
        liquidGrad.addColorStop(0, "#f0a830");
        liquidGrad.addColorStop(0.35, "#d4861a");
        liquidGrad.addColorStop(1, "#9a5a08");
        ctx.fillStyle = liquidGrad;

        ctx.beginPath();
        ctx.moveTo(liquidPoly[0][0], liquidPoly[0][1]);
        for (let i = 1; i < liquidPoly.length; i++) {
          ctx.lineTo(liquidPoly[i][0], liquidPoly[i][1]);
        }
        ctx.closePath();
        ctx.fill();

        const t = Date.now();
        const tx = -gyn;
        const ty = gxn;
        const lineLen = Math.hypot(W, H) * 1.4;
        const topAmp = 11;
        const botAmp = 5;
        const foamTop = -42;
        const foamBot = 10;

        ctx.beginPath();
        const steps = 80;
        for (let i = 0; i <= steps; i++) {
          const s = (i / steps - 0.5) * lineLen;
          const wave = Math.sin(s * 0.021 + t * 0.001) * topAmp;
          const off = foamTop + wave;
          const px = surfaceCenterX + tx * s + gxn * off;
          const py = surfaceCenterY + ty * s + gyn * off;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        for (let i = steps; i >= 0; i--) {
          const s = (i / steps - 0.5) * lineLen;
          const wave = Math.sin(s * 0.019 + t * 0.0008) * botAmp;
          const off = foamBot + wave;
          const px = surfaceCenterX + tx * s + gxn * off;
          const py = surfaceCenterY + ty * s + gyn * off;
          ctx.lineTo(px, py);
        }
        ctx.closePath();

        const foamGradTopX = surfaceCenterX + gxn * (foamTop - topAmp);
        const foamGradTopY = surfaceCenterY + gyn * (foamTop - topAmp);
        const foamGradBotX = surfaceCenterX + gxn * (foamBot + botAmp);
        const foamGradBotY = surfaceCenterY + gyn * (foamBot + botAmp);
        const foamGrad = ctx.createLinearGradient(
          foamGradTopX,
          foamGradTopY,
          foamGradBotX,
          foamGradBotY
        );
        foamGrad.addColorStop(0, "rgba(255,255,255,0.97)");
        foamGrad.addColorStop(0.45, "rgba(255,248,225,0.90)");
        foamGrad.addColorStop(0.75, "rgba(240,200,80,0.70)");
        foamGrad.addColorStop(1, "rgba(230,168,30,0.40)");
        ctx.fillStyle = foamGrad;
        ctx.fill();

        const bs = bubblesRef.current;
        for (let i = 0; i < bs.length; i++) {
          const b = bs[i];
          let bxPix = b.x * W;
          let byPix = b.y * H;
          bxPix -= gxn * b.speed * 1100;
          byPix -= gyn * b.speed * 1100;

          const depth = (bxPix - cx) * gxn + (byPix - cy) * gyn;
          if (
            depth < threshold + 10 ||
            bxPix < -20 ||
            bxPix > W + 20 ||
            byPix < -20 ||
            byPix > H + 20
          ) {
            const sOff = (Math.random() - 0.5) * lineLen * 0.7;
            const dOff = threshold + 80 + Math.random() * H * 0.4;
            bxPix = cx + tx * sOff + gxn * dOff;
            byPix = cy + ty * sOff + gyn * dOff;
          }
          b.x = bxPix / W;
          b.y = byPix / H;

          if (bxPix > 0 && bxPix < W && byPix > 0 && byPix < H) {
            const dCheck = (bxPix - cx) * gxn + (byPix - cy) * gyn;
            if (dCheck > threshold + 10) {
              ctx.beginPath();
              ctx.arc(bxPix, byPix, b.r, 0, Math.PI * 2);
              ctx.strokeStyle = `rgba(255,200,80,${b.opacity + 0.1})`;
              ctx.lineWidth = 1;
              ctx.stroke();
              ctx.fillStyle = `rgba(255,220,100,${b.opacity * 0.3})`;
              ctx.fill();
            }
          }
        }
      }

      if (!isPouringRef.current) {
        const gE = gammaEffRef.current;
        const bE = betaEffRef.current;

        // ─── DRAIN LOGIC ─────────────────────────────────────────────
        // Only drain when gravity is meaningfully pulling UP on screen.
        // gyn < -DRAIN_GY_START means the top of the phone is tipped
        // far enough down that liquid would physically spill out the
        // top edge of the screen. Pure sideways tilt keeps gyn near 0
        // (or even positive), so the liquid just pools on the side and
        // sits there stable — no drain. ✓
        let drainAmount = 0;
        if (forceDrainRef.current) {
          drainAmount = DRAIN_RATE * 0.35;
        } else if (gyn < -DRAIN_GY_START) {
          const pull = Math.min(
            1,
            (-gyn - DRAIN_GY_START) / (DRAIN_GY_FULL - DRAIN_GY_START)
          );
          drainAmount = DRAIN_RATE * pull * pull;
        }
        if (drainAmount > 0) {
          targetVolRef.current = Math.max(
            0,
            targetVolRef.current - drainAmount
          );
        }

        // Debug-friendly metric: 0–100 scale of how strongly gravity
        // pulls toward the top of the screen.
        const drainPull = gyn < 0 ? Math.min(1, -gyn) * 100 : 0;

        debugUpdateRef.current++;
        if (debugUpdateRef.current % 6 === 0) {
          setDebugInfo((prev) => ({
            ...prev,
            gammaEff: gE,
            betaEff: bE,
            gravX: gxn,
            gravY: gyn,
            drainPull,
            drainAmount,
            vol,
          }));
        }

        if (!drainDoneRef.current && vol < 0.02) {
          drainDoneRef.current = true;
          if (navigator.vibrate) navigator.vibrate([80, 60, 80]);
          setShowToast(true);
          setTimeout(() => setShowContinueBtn(true), 2200);
        }
      } else {
        debugUpdateRef.current++;
        if (debugUpdateRef.current % 6 === 0) {
          setDebugInfo((prev) => ({ ...prev, vol }));
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ─── SENSOR HANDLER ──────────────────────────────────────────────────────
  useEffect(() => {
    if (iosPermission !== "granted") return;

    const handler = (e) => {
      if (isDrainingRef.current) return;
      const gammaRaw = e.gamma ?? 0;
      const betaRaw = e.beta ?? 0;

      const flipGuard = Math.abs(gammaRaw) > GAMMA_FLIP_GUARD;
      if (flipGuard) {
        setDebugInfo((prev) => ({
          ...prev,
          gammaRaw,
          betaRaw,
          flipGuard: true,
        }));
        return;
      }

      if (!calibratedRef.current) {
        const samples = calibSamplesRef.current;
        samples.gamma.push(gammaRaw);
        samples.beta.push(betaRaw);
        if (samples.gamma.length >= CALIB_SAMPLES) {
          const avg = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
          baseGammaRef.current = avg(samples.gamma);
          baseBetaRef.current = avg(samples.beta);
          calibratedRef.current = true;
        }
        setDebugInfo((prev) => ({
          ...prev,
          gammaRaw,
          betaRaw,
          calibCount: samples.gamma.length,
          flipGuard: false,
        }));
        return;
      }

      if (isPouringRef.current) {
        setDebugInfo((prev) => ({
          ...prev,
          gammaRaw,
          betaRaw,
          calib: true,
          calibCount: CALIB_SAMPLES,
          flipGuard: false,
        }));
        return;
      }

      const gammaRel = gammaRaw - baseGammaRef.current;
      const betaRel = betaRaw - baseBetaRef.current;

      const deadzone = (v, dz) =>
        Math.abs(v) < dz ? 0 : v > 0 ? v - dz : v + dz;
      const gammaEff = deadzone(gammaRel, TILT_DEADZONE);
      const betaEff = deadzone(betaRel, TILT_DEADZONE);

      gammaEffRef.current = gammaEff;
      betaEffRef.current = betaEff;

      const gRad = (gammaEff * Math.PI) / 180;
      const bRad = (betaEff * Math.PI) / 180;
      const gx = Math.sin(gRad);
      const gy = Math.cos(gRad) * Math.cos(bRad) + Math.sin(bRad);

      targetGravXRef.current = gx;
      targetGravYRef.current = gy;

      if (
        !hasVibratedRef.current &&
        (Math.abs(gammaEff) > 10 || Math.abs(betaEff) > 10)
      ) {
        hasVibratedRef.current = true;
        if (navigator.vibrate) navigator.vibrate(14);
      }

      setDebugInfo((prev) => ({
        ...prev,
        gammaRaw,
        betaRaw,
        gammaRel,
        betaRel,
        calib: true,
        calibCount: CALIB_SAMPLES,
        flipGuard: false,
      }));
    };

    window.addEventListener("deviceorientation", handler, true);
    return () => window.removeEventListener("deviceorientation", handler, true);
  }, [iosPermission]);

  const handleBrindar = () => {
    if (forceDrainRef.current || isPouringRef.current) return;
    forceDrainRef.current = true;
    isDrainingRef.current = true;
    setIsDraining(true);
    if (navigator.vibrate) navigator.vibrate([30, 20, 30]);
  };

  const fmt = (v) => (v == null ? "—" : v.toFixed(1));
  const fmt2 = (v) => (v == null ? "—" : v.toFixed(2));
  const DRAIN_START_DISPLAY = DRAIN_GY_START * 100;
  const pullColor =
    debugInfo.drainPull > DRAIN_GY_FULL * 100
      ? "#ff8888"
      : debugInfo.drainPull > DRAIN_START_DISPLAY
      ? "#ffaa66"
      : "#7BDCFB";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        height: "100dvh",
        zIndex: 200,
        background: "#1a0a2e",
        fontFamily: "Quicksand, sans-serif",
        overflow: "hidden",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <style>{`
        @keyframes cag-in { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes toast-pop { 0%{opacity:0;transform:scale(.85)} 60%{opacity:1;transform:scale(1.05)} 100%{opacity:1;transform:scale(1)} }
        @keyframes kari-bounce-in { 0%{opacity:0;transform:scale(.5) translateY(30px)} 65%{opacity:1;transform:scale(1.08) translateY(-6px)} 100%{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes kari-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-9px)} }
        @keyframes toast-slide-up { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        @keyframes btn-appear { 0%{opacity:0;transform:scale(.8)} 70%{opacity:1;transform:scale(1.06)} 100%{opacity:1;transform:scale(1)} }
        @keyframes sensor-pulse { 0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(176,106,179,0.5)} 50%{transform:scale(1.03);box-shadow:0 0 0 8px rgba(176,106,179,0)} }
      `}</style>

      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          display: showToast ? "none" : "block",
        }}
      />

      {SHOW_DEBUG && !showToast && (
        <div
          style={{
            position: "absolute",
            top: 60,
            left: 10,
            zIndex: 15,
            background: "rgba(0,0,0,0.55)",
            color: "#7BDCFB",
            padding: "8px 10px",
            borderRadius: 8,
            fontFamily: "monospace",
            fontSize: 10,
            lineHeight: 1.45,
            pointerEvents: "none",
            border: "1px solid rgba(123,220,251,0.3)",
          }}
        >
          <div style={{ color: "#f9a8d4", fontWeight: 700, marginBottom: 2 }}>
            {isPouring
              ? "🍺 POURING..."
              : !debugInfo.calib
              ? `CALIBRATING ${debugInfo.calibCount}/${CALIB_SAMPLES}...`
              : debugInfo.flipGuard
              ? "⚠ FLIP GUARD"
              : "● ACTIVE"}
          </div>
          <div>
            eff γ:{fmt(debugInfo.gammaEff)} β:{fmt(debugInfo.betaEff)}
          </div>
          <div>
            grav x:{fmt2(debugInfo.gravX)} y:{fmt2(debugInfo.gravY)}
          </div>
          <div style={{ color: pullColor, fontWeight: 700 }}>
            ↳ pull: {fmt(debugInfo.drainPull)} / {DRAIN_START_DISPLAY}
          </div>
          <div
            style={{ color: debugInfo.drainAmount > 0 ? "#ff6666" : "#7BDCFB" }}
          >
            drain: {fmt(debugInfo.drainAmount * 1000)}‰
          </div>
          <div>vol {fmt(debugInfo.vol * 100)}%</div>
        </div>
      )}

      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "15px",
          right: "15px",
          zIndex: 10,
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          color: "rgba(255,255,255,0.8)",
          fontSize: "18px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "bold",
          lineHeight: 1,
          background: "transparent",
          border: "none",
        }}
      >
        ✕
      </button>

      {/* Texts after pour */}
      {!showToast && showTexts && (
        <div
          style={{
            position: "absolute",
            top: "56px",
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
            paddingLeft: "24px",
            paddingRight: "24px",
            color: "white",
            zIndex: 3,
            animation: "cag-in 0.5s ease both",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "rgba(176,106,179,0.28)",
              border: "1.5px solid rgba(176,106,179,0.6)",
              borderRadius: "999px",
              padding: "5px 20px",
              fontSize: "13px",
              fontWeight: "800",
              color: "#e0b8ff",
              letterSpacing: "0.03em",
            }}
          >
            🔥 Streak: {streak}
          </div>
          <h1
            style={{
              fontSize: "clamp(22px,7vw,30px)",
              fontWeight: "900",
              textAlign: "center",
              lineHeight: 1.25,
              margin: 0,
              textShadow: "0 2px 12px rgba(124,58,237,0.45)",
              marginTop: "15px",
            }}
          >
            Te ganaste una
            <br />
            caguama, bb 👑
          </h1>
          <p
            style={{
              color: "#c4b5fd",
              fontSize: "13px",
              textAlign: "center",
              fontWeight: "600",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {streak} seguidas. Esa racha estuvo icónica.
          </p>
        </div>
      )}

      {/* Bottom area: sensor permission OR instructions */}
      {!showToast && showTexts && (
        <div
          style={{
            position: "absolute",
            bottom: "6%",
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            zIndex: 5,
            padding: "0 32px",
            animation: "cag-in 0.6s 0.2s ease both",
          }}
        >
          {/* ── Probing: show nothing yet (resolves in <500ms) ── */}
          {iosPermission === "checking" && null}

          {/* ── iOS: needs tap to request permission ── */}
          {iosPermission === "idle" && (
            <button
              onClick={requestIOSPermission}
              style={{
                background: "linear-gradient(135deg, #b06ab3, #7c3aed)",
                border: "none",
                borderRadius: "999px",
                padding: "14px 36px",
                color: "white",
                fontFamily: "Quicksand, sans-serif",
                fontWeight: "900",
                fontSize: "16px",
                cursor: "pointer",
                boxShadow: "0 4px 20px rgba(124,58,237,0.45)",
                animation: "sensor-pulse 2s ease-in-out infinite",
                letterSpacing: "0.02em",
              }}
            >
              🍺 Activar sensor para tomar
            </button>
          )}

          {/* ── Waiting for iOS permission dialog ── */}
          {iosPermission === "asking" && (
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                fontWeight: "700",
                color: "rgba(255,255,255,0.7)",
                textAlign: "center",
              }}
            >
              Acepta el permiso arriba 👆
            </p>
          )}

          {/* ── Permission granted OR not iOS: show normal instructions ── */}
          {(iosPermission === "granted" || iosPermission === "unavailable") && (
            <p
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "800",
                color: "white",
                textAlign: "center",
                textShadow: "0 1px 8px rgba(0,0,0,0.4)",
              }}
            >
              📱 Inclina tu cel para tomar
            </p>
          )}

          {/* ── Denied: explain + fallback button ── */}
          {iosPermission === "denied" && (
            <p
              style={{
                margin: 0,
                fontSize: "11px",
                fontStyle: "italic",
                fontWeight: "600",
                color: "rgba(255,255,255,0.45)",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              Sin permiso al sensor el tilt no funciona,
              <br />
              pero igual puedes tocar abajo 🍺
            </p>
          )}

          {/* Fallback tap-to-drain button — visible once we've settled state */}
          {iosPermission !== "asking" && iosPermission !== "checking" && (
            <button
              onClick={handleBrindar}
              disabled={isDraining}
              style={{
                marginTop: "3px",
                background: "transparent",
                border: "none",
                borderBottom: "1.5px dotted rgba(255,255,255,0.65)",
                padding: "2px 2px 3px",
                color: isDraining
                  ? "rgba(255,255,255,0.35)"
                  : "rgba(255,255,255,0.78)",
                fontFamily: "Quicksand, sans-serif",
                fontWeight: "700",
                fontSize: "12px",
                fontStyle: "italic",
                cursor: isDraining ? "default" : "pointer",
                textShadow: "0 1px 5px rgba(0,0,0,0.25)",
                transition: "opacity 0.2s, transform 0.15s, color 0.2s",
                transform: isDraining ? "scale(0.98)" : "scale(1)",
              }}
            >
              ¿no puedes inclinar? toca aquí, bb
            </button>
          )}
        </div>
      )}

      {/* Toast / completion screen */}
      {showToast && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#1a0a2e",
            animation: "toast-pop 0.4s ease both",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          <img
            src="https://res.cloudinary.com/dbvqeiqxb/image/upload/v1780091865/ChatGPT_Image_May_29_2026_04_57_15_PM_rs1qqr.png"
            alt="Kari brindando"
            style={{
              width: "min(230px,68vw)",
              marginBottom: "20px",
              animation:
                "kari-bounce-in 0.65s cubic-bezier(0.34,1.56,0.64,1) 0.1s both, kari-float 3s 0.85s ease-in-out infinite",
              filter: "drop-shadow(0 8px 28px rgba(124,58,237,0.45))",
            }}
          />
          <h1
            style={{
              color: "white",
              fontSize: "clamp(22px,7vw,30px)",
              fontWeight: "900",
              textAlign: "center",
              margin: "0 0 10px",
              lineHeight: 1.2,
              textShadow: "0 2px 16px rgba(124,58,237,0.6)",
              animation: "toast-slide-up 0.45s 0.45s ease both",
            }}
          >
            {toastMessage.title}
          </h1>
          <p
            style={{
              color: "#c4b5fd",
              fontSize: "14px",
              fontWeight: "600",
              textAlign: "center",
              margin: 0,
              lineHeight: 1.55,
              maxWidth: "280px",
              animation: "toast-slide-up 0.45s 0.6s ease both",
            }}
          >
            {toastMessage.subtitle}
          </p>
          <button
            onClick={onClose}
            style={{
              marginTop: "40px",
              background: "white",
              border: "none",
              borderRadius: "999px",
              padding: "14px 52px",
              color: "#7c3aed",
              fontFamily: "Quicksand, sans-serif",
              fontWeight: "900",
              fontSize: "17px",
              cursor: "pointer",
              boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              letterSpacing: "0.02em",
              animation: "btn-appear 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
            }}
          >
            ✨ Que siga el juego
          </button>
        </div>
      )}
    </div>
  );
}

// ─── GEOMETRY HELPERS ───────────────────────────────────────────────────────

function clipHalfPlane(poly, nx, ny, threshold, cx, cy) {
  const depth = (p) => (p[0] - cx) * nx + (p[1] - cy) * ny - threshold;
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const curr = poly[i];
    const prev = poly[(i + poly.length - 1) % poly.length];
    const dC = depth(curr);
    const dP = depth(prev);
    const cIn = dC >= 0;
    const pIn = dP >= 0;
    if (cIn) {
      if (!pIn) {
        const t = dP / (dP - dC);
        out.push([
          prev[0] + t * (curr[0] - prev[0]),
          prev[1] + t * (curr[1] - prev[1]),
        ]);
      }
      out.push(curr);
    } else if (pIn) {
      const t = dP / (dP - dC);
      out.push([
        prev[0] + t * (curr[0] - prev[0]),
        prev[1] + t * (curr[1] - prev[1]),
      ]);
    }
  }
  return out;
}

function polyArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

function findSurfaceThreshold(W, H, cx, cy, nx, ny, volFraction) {
  const total = W * H;
  const target = volFraction * total;
  const halfExtent = (Math.abs(nx) * W + Math.abs(ny) * H) / 2 + 1;
  let lo = -halfExtent;
  let hi = halfExtent;
  const screenRect = [
    [0, 0],
    [W, 0],
    [W, H],
    [0, H],
  ];
  for (let i = 0; i < 14; i++) {
    const mid = (lo + hi) / 2;
    const poly = clipHalfPlane(screenRect, nx, ny, mid, cx, cy);
    const area = polyArea(poly);
    if (area > target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}
